import Vue from "vue";
import VueRouter, { NavigationGuardNext, Route, RouteConfig } from "vue-router";
import i18n from "@/i18n";
import Home from "../views/Home.vue";
import NotFound from "../views/NotFound.vue";
import { PositionResult } from "vue-router/types/router";
import { parseLocaleCode } from "@/models/Locale";

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
    {
        path: "/:lang?/home",
        alias: ['/:lang?/', '/'],
        name: "Home",
        component: Home,
    },
    {
        path: "/:lang?/home/idea",
        name: "HomeFirstIdea",
        component: () => import("../views/HomeFirstIdea.vue"),
    },
    {
        path: "/:lang?/home/application",
        name: "HomeApplication",
        component: () => import("../views/HomeApplication.vue"),
    },
    {
        path: "/:lang?/home/monitor",
        name: "HomeMonitor",
        component: () => import("../views/HomeMonitor.vue"),
    },
    {
        path: "/:lang?/insights",
        name: "Insights",
        component: () => import("../views/Insights.vue"),
    },
    {
        path: "/:lang?/products",
        name: "Products",
        component: () => import("../views/Products.vue"),
    },
    {
        path: "/:lang?/search",
        name: "Search",
        component: () => import("../views/Search.vue"),
        props: (route) => ({ initialBrandName: route.query.q }),
    },
    {
        path: "/:lang?/brands/:id",
        name: "Brand",
        component: () => import("../views/Brand.vue"),
        props: (route) => ({ id: route.params.id })
    },
    {
        path: "/:lang?/chat",
        name: "Chat",
        component: () => import("../views/Chat.vue"),
    },
    {
        path: "/:lang?/check",
        name: "Create",
        component: () => import("../views/Create.vue"),
    },
    {
        path: "/:lang?/monitorold",
        name: "Monitor",
        component: () => import("../views/Monitor.vue"),
    },
    {
        path: "/:lang?/monitor",
        name: "Monitorings",
        component: () => import("../views/Monitoring/Overview.vue"),
    },
    {
        path: "/:lang?/monitor/:id",
        name: "Monitoring",
        component: () => import("../views/Monitoring/Dashboard.vue"),
        props: (route) => ({ id: route.params.id })
    },
    {
        path: "/:lang?/monitor/create/brand",
        name: "CreateBrandMonitoring",
        component: () => import("../views/Monitoring/CreateBrandMonitoring.vue")
    },
    {
        path: "/:lang?/mybraendz",
        name: "MyBraendz",
        component: () => import("../views/MyBraendz.vue"),
    },
    {
        path: "/:lang?/storedqueries/:queryId/results/:resultId",
        name: "StoredBrandQueryResult",
        component: () => import("../views/StoredBrandQueryResult.vue"),
        props: (route) => ({ 
            storedBrandQueryId: route.params.queryId,
            resultId: route.params.resultId
         })
    },
    {
        path: "/:lang?/subscribe/marketplace",
        name: "SubscribeMarketplace",
        component: () => import("../views/SubscribeMarketplace.vue"),
        props: (route) => ({ 
            token: route.query.token,
            initialStep: !route.query.step || Number.isNaN(route.query.step) ? 1 : Number(route.query.step), 
            packageKeys: Array.isArray(route.query.packages) ? route.query.packages
                : route.query.packages ? [route.query.packages] : []
        }),
    },
    {
        path: "/:lang?/subscribe/:product",
        name: "Subscribe",
        component: () => import("../views/Subscribe.vue"),
        props: (route) => ({ 
            productKey: route.params.product, 
            initialStep: !route.query.step || Number.isNaN(route.query.step) ? 1 : Number(route.query.step), 
            packageKeys: Array.isArray(route.query.packages) ? route.query.packages
                : route.query.packages ? [route.query.packages] : []
        }),
    },
    {
        path: "/:lang?/terms",
        name: "Terms",
        component: () => import("../views/Terms.vue"),
    },
    {
        path: "/:lang?/imprint",
        name: "Imprint",
        component: () => import("../views/Imprint.vue"),
    },
    {
        path: "/:lang?/privacy",
        name: "Privacy",
        component: () => import("../views/Privacy.vue"),
    },
    {
        path: "/:lang?/feedback",
        name: "Feedback",
        component: () => import("../views/Feedback.vue"),
    },
    {
        path: "/:lang?/test",
        name: "Test",
        component: () => import("../views/Test.vue"),
    },
    {
        path: "/:lang?/404",
        name: "NotFound",
        component: NotFound,
    }
];

// Add promo route if authentication is enabled
if (process.env.VUE_APP_PROMO_ENABLED === 'true') {
    routes.push({
        path: '/:lang?/promo',
        name: 'ProductsPromo',
        component: () => import('../views/ProductsPromo.vue')
    });
}

// Add the NotFound route at the end to catch all unmatched routes
routes.push({
    path: "/:lang?/*",
    component: NotFound,
}, {
    path: "*",
    component: NotFound,
});

const router = new VueRouter({
    mode: "history",
    base: process.env.BASE_URL,
    routes,
    scrollBehavior: (to, from, savedPosition) => {
        // If the route is the same, do not scroll
        if (to.path === from.path && to.hash === from.hash) {
            return;
        }

        // If there is a saved position (e.g., from back navigation), return it
        if (savedPosition) {
            return { y: savedPosition.y } as PositionResult;
        }

        // If the route has a hash that starts with "#state=", scroll to the element with that ID
        if (to.hash && !to.hash.startsWith("#state=")) {
            return { behavior: "smooth", selector: to.hash } as PositionResult;
        }

        // Scroll to the top of the page
        return { y: 0 } as PositionResult;
    }
});

router.beforeEach((to, from, next) => {
    // Check if the lang parameter is present in the route and continue navigation:
    const lang = to.params.lang;

    if(!lang) {
        // Lang is not set, just continue with the default locale (navigator locale):
        return next();
    }
    else if(lang.length > 2 && lang.indexOf("-") === -1) {
        // Hack: This needs to be checked first, otherwise e.g. "/products" will be detected as a valid lang. 
        // So, this lang seems to be a page name.
        return next({path: `/${i18n.locale}${to.fullPath}`});
    }
    else {
        const locale = parseLocaleCode(lang);

        if(!locale) {
            // Lang is not a valid locale code, redirect to the default locale:
            console.warn(`Lang '${lang}' seems not to be a valid locale. Redirecting to this page with the default locale.`);
            return next({path: `/${i18n.locale}${to.fullPath}`});
        }
        else if(i18n.availableLocales.includes(locale.baseName)) {
            // Lang is a valid locale and available, set it as the current locale:
            i18n.locale = locale.baseName;
            return next();
        }
        else if(i18n.availableLocales.includes(locale.language)) {
            // Lang is a valid locale but not available, set it to the neutral language:
            i18n.locale = locale.language;
            return next();
        }
        else {
            // Lang is a valid locale but not available, redirect to the fallback locale:
            const defaultLocale = i18n.fallbackLocale as string;
            console.warn(`Locale ${lang} is not available, redirecting to ${defaultLocale}`);
            i18n.locale = defaultLocale;

            return next({
                name: to.name || undefined,
                params: { ...to.params, lang: defaultLocale },
                query: to.query,
                hash: to.hash,
                replace: true
            });
        }
    }
});

export default router;
